/**
 * Workspace Settings → Danger. Soft-delete only for v1.
 *
 * Owner+admin only. Soft-delete is recoverable for 30 days from the
 * recovery page (Phase 5c-2); after that, the cron-driven hard
 * delete cascades through every workspace-scoped collection.
 */
import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '@/components/ui'
import type { WorkspaceSummary } from '@/lib/api'
import { workspacesAPI } from '@/lib/api'
import { toUserMessage } from '@/lib/userMessage'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { useCanIWorkspace } from '@/hooks/useCanI'
import { useIdentity } from '@/hooks/useIdentity'

export function WorkspaceDanger({ workspace }: { workspace: WorkspaceSummary }) {
  const navigate = useNavigate()
  const { state } = useIdentity()
  const canDelete = useCanIWorkspace('workspaces.delete', workspace._id)
  // #2510: 0 = no hold, deletion is permanent on the spot.
  const recoveryWindowDays =
    (state.status === 'ready' ? state.identity.recoveryWindowDays : 0) ?? 0
  const permanent = recoveryWindowDays === 0
  const [showConfirm, setShowConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!canDelete) {
    return (
      <p className="text-sm text-muted-foreground">
        Only owners and admins can delete this workspace.
      </p>
    )
  }

  async function handleDelete() {
    setError(null)
    setBusy(true)
    try {
      await workspacesAPI.softDelete(workspace._id)
      // Workspace is gone from /api/workspaces/me's eligible set;
      // bounce to / so the IdentityGate can pick the next available
      // workspace (or send to /onboarding if this was the only one).
      navigate('/', { replace: true })
    } catch (err) {
      setError(
        toUserMessage(
          err,
          "Couldn't delete this workspace. Try again. If it keeps failing, ask whoever runs OttoTester here to check the server log.",
        ),
      )
      setBusy(false)
    }
  }

  return (
    <div className="space-y-6 max-w-md">
      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold text-destructive">Delete this workspace</h2>
          <p className="text-sm text-muted-foreground">
            {permanent ? (
              <>
                All apps, variants, runs, and test data are permanently
                deleted immediately. This can&apos;t be undone.
              </>
            ) : (
              <>
                All apps, variants, runs, and test data become unreachable
                immediately. Recoverable for {recoveryWindowDays} days from
                the recovery page; after that, everything is permanently
                deleted.
              </>
            )}
          </p>
        </div>

        <Button
          variant="destructive"
          onClick={() => setShowConfirm(true)}
          disabled={busy}
        >
          Delete workspace
        </Button>

        {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

        {!permanent && (
          <p className="text-xs text-muted-foreground">
            Deleted by mistake?{' '}
            <Link to="/recover" className="underline hover:text-foreground">
              Recover
            </Link>
            {' '}within the recovery window.
          </p>
        )}
      </section>

      {showConfirm && (
        <ConfirmDialog
          open
          onClose={() => setShowConfirm(false)}
          title="Delete workspace"
          description={
            permanent ? (
              <>
                <strong>{workspace.name}</strong> and its test data,
                applications, and run history will be permanently deleted.
                This can&apos;t be undone.
              </>
            ) : (
              <>
                <strong>{workspace.name}</strong> will be deleted. Test data,
                applications, and run history all become unreachable. You have{' '}
                {recoveryWindowDays} days to recover.
              </>
            )
          }
          confirmLabel={permanent ? 'Delete permanently' : 'Delete workspace'}
          onConfirm={handleDelete}
          busy={busy}
        />
      )}
    </div>
  )
}
